import { useQuery, useMutation } from "@tanstack/react-query";
import { CreditCard, Lock, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";

interface AuthUser {
  id: string;
  cardRequired?: boolean;
  cardRequiredWorkspaceId?: string | null;
}

// Wave 2 — Free + 3% billing engine
// Blocking gate shown to Lite-tier workspaces older than 60 days that
// haven't saved a Stripe card. The gate is full-screen and prevents any
// in-app navigation until the user opens the card-save flow. The
// `cardRequired` flag is injected by /api/auth/user.
export function LiteCardRequiredGate() {
  const { data: user, isLoading } = useQuery<AuthUser>({
    queryKey: ["/api/auth/user"],
  });

  const startSetup = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/lite/save-card", {
        workspaceId: user?.cardRequiredWorkspaceId || undefined,
      });
      return res.json() as Promise<{ url?: string }>;
    },
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
  });

  if (isLoading || !user?.cardRequired) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-background/95 backdrop-blur-sm p-4"
      data-testid="gate-lite-card-required"
    >
      <div className="max-w-md w-full bg-card border border-border rounded-lg shadow-lg p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-950 flex items-center justify-center">
            <Lock className="w-5 h-5 text-amber-700 dark:text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Add a payment method to continue</h2>
            <p className="text-sm text-muted-foreground">Free trial period has ended</p>
          </div>
        </div>

        <div className="text-sm space-y-3 leading-relaxed">
          <p>
            Your workspace has been on the free plan for 60 days. To keep using
            Clarik, please save a card on file. We only charge a small
            percentage of revenue you collect through the platform — there is
            no monthly fee.
          </p>
          <div className="bg-muted/50 rounded-md p-3 text-xs flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
            <span>
              You will not be charged today. The card is only used when revenue
              share applies on a paid invoice.
            </span>
          </div>
        </div>

        <Button
          className="w-full"
          onClick={() => startSetup.mutate()}
          disabled={startSetup.isPending}
          data-testid="button-card-required-save"
        >
          {startSetup.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <CreditCard className="w-4 h-4 mr-2" />
          )}
          Save card on file
        </Button>
      </div>
    </div>
  );
}
